import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { ArrowUpRight, Check } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&h=1000&q=85";

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="page-about" className="bg-white text-[#0A0A0A]">
      {/* Hero */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        <div className="absolute -top-20 -right-32 w-[500px] h-[500px] bg-[#EA6A1A]/6 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-6 text-[#52525B]">
              — {t.about.eyebrow}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.03em] font-medium">
              {t.about.title.split(".")[0]}<span className="italic text-[#EA6A1A]">.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Split */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-6"
          >
            <div className="rounded-3xl overflow-hidden">
              <img src={ABOUT_IMG} alt="Beratung" className="w-full h-[500px] md:h-[580px] object-cover" />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-6"
          >
            <p className="font-display text-3xl md:text-4xl leading-[1.15] tracking-tight mb-8">
              {t.about.p1}
            </p>
            <p className="text-[#52525B] leading-[1.8] text-[16px]">{t.about.p2}</p>
          </motion.div>
        </div>
      </section>

      {/* Values — Modern cards */}
      <section className="bg-[#F7F5F0] py-24 md:py-32 border-y border-[#E9E9E7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-white border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
              — {t.about.valuesTitle}
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-medium">
              Was uns <span className="italic text-[#EA6A1A]">antreibt</span>.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {t.about.values.map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`value-${i}`}
                className="bg-white rounded-2xl p-8 border border-[#E9E9E7] card-shadow group hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-11 h-11 bg-[#F5F5F4] group-hover:bg-[#EA6A1A] rounded-xl flex items-center justify-center transition-colors">
                    <Check className="text-[#0A0A0A] group-hover:text-white" size={18} strokeWidth={2.5} />
                  </div>
                  <span className="text-[#EA6A1A] font-display text-2xl font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-medium mb-3 tracking-tight">{v.t}</h3>
                <p className="text-[#71717A] leading-relaxed text-[15px]">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 grid md:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-9"
          >
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-medium">
              {t.hero.subtitle.split(".")[0]}<span className="italic text-[#EA6A1A]">.</span>
            </h2>
          </motion.div>
          <div className="md:col-span-3 md:text-right">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white px-7 py-4 rounded-full transition-all shadow-sm"
              data-testid="about-cta"
            >
              <span className="text-sm font-medium">{t.nav.cta}</span>
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
